'use client';

import { Fragment, ReactElement, useContext, useState } from 'react';
import { Controller, FieldError, get } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';

import { getFacetCode } from '@lamesarv-sdk/tools';

import { PropsWithClasses } from '@lamesarv-sdk/types';

import { CommonFormContext } from '.';

interface ICommonSelectProps extends PropsWithClasses {
  name: string;
  options: { title: string; value: string }[];
  optionsCustomDesktop?: ReactElement;
  optionsCustomMobile?: ReactElement;
  optionsCustomGetLabel?: (value: string) => string;
  label?: string;
  hideLabel?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  useValueToDisplay?: boolean;
  classContainer?: string;
  classLabel?: string;
  classLabelWithValue?: string;
  classInputWithValue?: string;
}

export const CommonSelect = (props: ICommonSelectProps) => {
  const [value, setValue] = useState<string>();
  const { isDisabled } = useContext(CommonFormContext);

  const getLabel = (value: string) => {
    return props.options.find((option) => option.value === value)?.title || '';
  };

  return (
    <div className={twMerge('flex flex-col gap-1.5 group', isDisabled && 'opacity-60', props.classContainer)}>
      {!props.hideLabel && (
        <span className={twMerge('text-sm text-black-500', props.classLabel, !!value && props.classLabelWithValue)}>
          {props.label}
          {props.isRequired && <span className="ml-1 text-red-700 leading-none">*</span>}
        </span>
      )}
      <Controller
        name={props.name}
        defaultValue=""
        render={({ field, fieldState, formState: { errors } }) => {
          const error = get(errors, props.name) as FieldError | null;
          return (
            <Fragment>
              <input type="hidden" name={field.name} value={field.value} />
              <div className="hidden sm:block">
                <Listbox
                  value={field.value}
                  onChange={(value: string) => {
                    field.onChange(value);
                    setValue(value);
                  }}
                >
                  {({ open }) => {
                    const buttonDisplay =
                      (props.useValueToDisplay
                        ? field.value
                        : props.optionsCustomGetLabel
                          ? props.optionsCustomGetLabel(field.value)
                          : getLabel(field.value)) ||
                      (props.placeholder ?? 'Select a location');

                    return (
                      <div className="relative">
                        {open && <div className="fixed inset-0 z-100 bg-black bg-opacity-30"></div>}
                        <ListboxButton
                          className={twMerge(
                            'border border-black-200 rounded-sm px-3 py-2 placeholder-black-350 text-black-500 w-full',
                            props.classInput,
                            !!value && props.classInputWithValue,
                            fieldState.error && 'border-red-700 placeholder-red-400 text-red-700',
                          )}
                        >
                          <span
                            className={twMerge(
                              'block truncate text-start',
                              field.value ? 'text-black-500' : 'text-black-350',
                            )}
                          >
                            {buttonDisplay}
                            &nbsp;
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={twMerge('h-4 w-4 text-black-350 transition', open && 'rotate-180')}
                              aria-hidden="true"
                            />
                          </span>
                        </ListboxButton>
                        <Transition
                          show={open}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <ListboxOptions
                            aria-orientation="vertical"
                            aria-labelledby="menu-button"
                            modal={false}
                            className={twMerge(
                              'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-200 w-80 max-h-[60%] overflow-y-auto',
                              'divide-y divide-black-100 rounded bg-white shadow-lg border border-black-100 focus:outline-none',
                            )}
                          >
                            {props.optionsCustomDesktop}
                            {props.options.map((option) => {
                              const key = getFacetCode(option.value);
                              return (
                                <ListboxOption
                                  key={key}
                                  value={option.value}
                                  className="px-4 py-2 text-sm text-black-500 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                                >
                                  {option.title}
                                </ListboxOption>
                              );
                            })}
                          </ListboxOptions>
                        </Transition>
                      </div>
                    );
                  }}
                </Listbox>
              </div>
              <div className="block sm:hidden">
                <select
                  className={twMerge('w-full', props.classInput, !!value && props.classInputWithValue)}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue(e.target.value);
                  }}
                >
                  <option value="" disabled>
                    {props.placeholder ?? 'Select a location'}
                  </option>
                  {props.optionsCustomMobile}
                  {props.options.map((option) => {
                    const key = getFacetCode(option.value);
                    return (
                      <option key={key} value={option.value}>
                        {option.title}
                      </option>
                    );
                  })}
                </select>
              </div>
              {error && <span className="text-left text-red-700 text-sm">{error.message}</span>}
            </Fragment>
          );
        }}
      />
    </div>
  );
};
